import { ref, watch, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { FirestoreError, collection, onSnapshot } from "firebase/firestore";
import { paymentsFirestore } from "@/core/firebase";
import { PATH } from "@/core/constants/path";
import { formatStringToRoutePath } from "@/core/utils/helpers";
import { paymentsBus, PAYMENT_EVENTS } from "@/core/eventBusses/payments";

export interface PaymentRealtimeItem {
  id: string;
}

function playNotificationSound() {
  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctx) return;
  const ctx = new Ctx();

  const playTone = (frequency: number, startTime: number, duration: number) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = frequency;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.15, startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
    osc.start(startTime);
    osc.stop(startTime + duration);
  };

  const play = () => {
    try {
      playTone(523.25, 0, 0.08);
      playTone(659.25, 0.12, 0.08);
      playTone(783.99, 0.24, 0.12);
    } catch {
      // ignore if audio not supported or blocked
    }
  };

  if (ctx.state === "suspended") {
    void ctx.resume().then(play);
  } else {
    play();
  }
}

export function usePaymentRealtimeAlerts() {
  const router = useRouter();
  const route = useRoute();

  const realtimePaymentAlerts: Ref<PaymentRealtimeItem[]> = ref([]);
  const unsubscribePaymentsListener = ref<(() => void) | null>(null);
  const hasLoadedInitialPaymentsSnapshot = ref(false);
  const hasRealtimeListenerError = ref(false);
  const knownPaymentIds = new Set<string>();

  const addRealtimePaymentAlert = (paymentId: string) => {
    const current = realtimePaymentAlerts.value;
    const existingIndex = current.findIndex((item) => item.id === paymentId);

    if (existingIndex !== -1) {
      const next = [...current];
      next.splice(existingIndex, 1);
      realtimePaymentAlerts.value = next;
    }

    const next = [{ id: paymentId }, ...realtimePaymentAlerts.value];
    realtimePaymentAlerts.value = next.length > 8 ? next.slice(0, 8) : next;
    playNotificationSound();
  };

  const onOpenRealtimeAlert = (paymentId: string) => {
    router.push(
      formatStringToRoutePath(PATH.PAYMENT_DETAIL, { id: paymentId }),
    );
    realtimePaymentAlerts.value = realtimePaymentAlerts.value.filter(
      (item) => item.id !== paymentId,
    );
  };

  const onClearRealtimeAlerts = () => {
    realtimePaymentAlerts.value = [];
  };

  watch(
    () => ({ path: route.path, id: route.params.id }),
    ({ path, id }) => {
      if (id && path.includes("/payments/detail")) {
        onClearRealtimeAlerts();
      }
    },
  );

  const setupPaymentsRealtimeListener = () => {
    if (!paymentsFirestore || hasRealtimeListenerError.value) {
      return;
    }

    unsubscribePaymentsListener.value = onSnapshot(
      collection(paymentsFirestore, "payments"),
      (snapshot) => {
        if (!hasLoadedInitialPaymentsSnapshot.value) {
          snapshot.docs.forEach((doc) => knownPaymentIds.add(doc.id));
          hasLoadedInitialPaymentsSnapshot.value = true;
          return;
        }

        const newChange = snapshot
          .docChanges()
          .find(
            (change) =>
              change.type === "added" && !knownPaymentIds.has(change.doc.id),
          );

        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            knownPaymentIds.add(change.doc.id);
          }
        });

        if (newChange) {
          paymentsBus.emit(PAYMENT_EVENTS.RELOAD_LIST);
          if (!route.params.id) {
            addRealtimePaymentAlert(newChange.doc.id);
          }
        }
      },
      (error: FirestoreError) => {
        if (error.code === "permission-denied") {
          hasRealtimeListenerError.value = true;
        }
        if (unsubscribePaymentsListener.value) {
          unsubscribePaymentsListener.value();
          unsubscribePaymentsListener.value = null;
        }
      },
    );
  };

  const teardownPaymentsRealtimeListener = () => {
    if (unsubscribePaymentsListener.value) {
      unsubscribePaymentsListener.value();
      unsubscribePaymentsListener.value = null;
    }
  };

  return {
    realtimePaymentAlerts,
    onOpenRealtimeAlert,
    onClearRealtimeAlerts,
    setupPaymentsRealtimeListener,
    teardownPaymentsRealtimeListener,
  };
}
